import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { navigate } from 'builder/modules/navigate'
import { apiClient, baseClient } from 'builder/modules/apiClient'
import { ColumnName } from 'builder/modules/jobTracking'
import Spinner from 'builder/components/Spinner'
import { getJobById } from 'builder/components/FindJob/utils'
import { CreateJobPostingResponse } from 'builder/modules/resumeEditor/types'
import { useI18n } from 'builder/hooks/useI18n'
import { PostSignupMode } from 'builder/modules/constants'
import { Container, Message } from './styles'

const PostSignup: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(true)
  const { i18n } = useI18n()

  useEffect(() => {
    const handlePostRegistration = async () => {
      setIsLoading(true)
      const mode = searchParams.get('mode')
      let redirectPath = '/'

      switch (mode) {
        case PostSignupMode.autoTailorFromJobBoard:
          const jobId = searchParams.get('job_id')
          if (!jobId) {
            break
          }
          try {
            const [resumesResponse, job] = await Promise.all([
              apiClient.get('/resumes'),
              getJobById(jobId),
            ])
            const resumes = resumesResponse.data.resumes
            if (resumes.length > 1) {
              setIsLoading(false)
              navigate('/', { replace: true })
              return
            }
            const [cardResponse, jobPostingResponse, newResumeResponse] = await Promise.all([
              apiClient.post('/job-board/cards', {
                status: ColumnName.wishlist,
                title: job?.job_title,
                company: job?.company,
                link: job?.url,
                description: job?.description,
                external_slug_id: job?.external_slug_id,
                location: job?.location,
                logo: job?.company_logo,
              }),

              baseClient.post<CreateJobPostingResponse>('/tailored_resumes/create_job_posting', {
                job_posting: {
                  description: job.description,
                  job_title: job.job_title,
                  company_name: job.company,
                },
              }),
              resumes.length > 0
                ? Promise.resolve({ data: resumes[0] })
                : apiClient.post('/resumes', {
                    template: 'london',
                  }),
            ])
            const resume = newResumeResponse.data
            const resumeId = resume.id
            const jobPostingId = jobPostingResponse.data.jobPosting.id
            const cardId = cardResponse.data.jobCard.id
            const userId = resume.userId

            await Promise.all([
              baseClient.post('/job-postings/update-job-posting', null, {
                params: {
                  job_url: job.url,
                  resume_id: resumeId,
                  is_job_tracker: true,
                },
              }),
              baseClient.post('/job-postings/update_selected_resume', {
                user_id: userId,
                resume_id: resumeId,
                job_posting_id: jobPostingId,
                job_card_id: cardId,
              }),
            ])
            redirectPath = `/resumes/${resumeId}/edit?mode=auto_tailoring_from_public_job_board`
          } catch (error) {
            console.error('Auto tailoring flow failed:', error)
          }
          break
        case PostSignupMode.landing:
          const resumesResponse = await apiClient.get('/resumes')
          const resumes = resumesResponse.data.resumes
          if (resumes.length > 0) {
            redirectPath = `/resumes/${resumes[0].id}/edit`
          } else {
            const templateId = searchParams.get('template_id') || 'london'
            const { data: newResume } = await apiClient.post('/resumes', {
              template: templateId,
            })
            redirectPath = `/resumes/${newResume.id}/edit`
          }
          break
        case PostSignupMode.saveJobFromJobBoard:
          const jobIdToSave = searchParams.get('job_id')
          if (!jobIdToSave) {
            break
          }
          try {
            const job = await getJobById(jobIdToSave)
            await apiClient.post('/job-board/cards', {
              status: ColumnName.wishlist,
              title: job?.job_title,
              company: job?.company,
              link: job?.url,
              description: job?.description,
              external_slug_id: job?.external_slug_id,
              location: job?.location,
              logo: job?.company_logo,
            })
            redirectPath = '/job-tracking'
          } catch (error) {
            console.error('Saving job to job tracker failed:', error)
          }
          break
        default:
          break
      }
      navigate(redirectPath, { replace: true })
    }

    handlePostRegistration()
  }, [searchParams])

  if (isLoading) {
    return (
      <Container>
        <Spinner />
        <Message>{i18n.t('builder.post_signup.message')}</Message>
      </Container>
    )
  }

  return null
}

export default PostSignup
